import { WebPushConfig } from "@aitisal/contracts/notification";
import { createPushSubscription } from "@aitisal/sdk/notification";

// Utility to convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = globalThis.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function getRegistration(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    return Promise.reject(new Error("Service Worker not supported"));
  }
  return navigator.serviceWorker.ready;
}

/**
 * Setup message listener for service worker communication.
 * Call this after order placement with the customerId from the order response.
 */
export function setupServiceWorkerMessageListener(
  userId: string,
  restaurantId: string,
) {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker not supported");
    return;
  }

  if (!userId || !restaurantId) {
    console.warn("userId and restaurantId are required for push notifications");
    return;
  }

  // Remove existing listener if any
  navigator.serviceWorker.removeEventListener(
    "message",
    handleServiceWorkerMessage as any,
  );

  // Get logo URL from meta tag injected by Cloudflare worker
  const logoMeta = document.querySelector(
    'meta[name="logo"]',
  ) as HTMLMetaElement | null;
  const logoUrl = logoMeta?.content || "";

  // Store identifiers for later use by the service worker via MessageChannel
  (window as any).__pushNotificationUserId = userId;
  (window as any).__pushNotificationRestaurantId = restaurantId;

  // Cache the logo URL so service worker can access it even without open windows
  if (logoUrl && "caches" in window) {
    caches
      .open("push-notification-config")
      .then((cache) => {
        cache.put("logo-url", new Response(logoUrl));
      })
      .catch(() => {});
  }

  // Add new listener
  navigator.serviceWorker.addEventListener(
    "message",
    handleServiceWorkerMessage as any,
  );
}

function handleServiceWorkerMessage(event: MessageEvent) {
  const { type } = event.data || {};
  switch (type) {
    case "GET_USER_ID": {
      const userId = (window as any).__pushNotificationUserId;
      if (event.ports && event.ports[0]) {
        event.ports[0].postMessage({ userId });
      }
      break;
    }
    case "GET_RESTAURANT_ID": {
      const restaurantId = (window as any).__pushNotificationRestaurantId;
      if (event.ports && event.ports[0]) {
        event.ports[0].postMessage({ restaurantId });
      }
      break;
    }
    default:
      // Ignore unknown messages
      break;
  }
}

export async function subscribeUserToPush(
  forceNew: boolean = false,
): Promise<PushSubscription | null> {
  try {
    const registration = await getRegistration();
    const existing = await registration.pushManager.getSubscription();

    const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;
    if (!vapidKey) {
      console.error("VITE_VAPID_PUBLIC_KEY missing; cannot subscribe to push.");
      throw new Error("VAPID key not configured");
    }

    // If there's an existing subscription, check if it uses the same VAPID key
    if (existing) {
      if (!forceNew) {
        // Check if the existing subscription's applicationServerKey matches our VAPID key
        const existingKey = existing.options?.applicationServerKey;
        if (existingKey) {
          const currentKeyArray = urlBase64ToUint8Array(vapidKey);
          const existingKeyArray = new Uint8Array(existingKey as ArrayBuffer);
          const keysMatch =
            currentKeyArray.length === existingKeyArray.length &&
            currentKeyArray.every((val, i) => val === existingKeyArray[i]);

          if (keysMatch) {
            return existing;
          }
          // Keys don't match - need to unsubscribe and create new
          console.log("VAPID key changed, re-subscribing...");
        } else {
          // Can't verify key, return existing
          return existing;
        }
      }

      // Unsubscribe from existing
      try {
        await existing.unsubscribe();
        console.log("Unsubscribed from old push subscription");
      } catch (err) {
        console.warn("Failed to unsubscribe old subscription:", err);
      }
    }

    const applicationServerKey = urlBase64ToUint8Array(vapidKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    return subscription;
  } catch (err: any) {
    console.error("Failed to subscribe to push:", err);

    // If AbortError, try to unsubscribe any existing subscription and retry once
    if (err?.name === "AbortError") {
      try {
        const registration = await getRegistration();
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
          console.log("AbortError - attempting to unsubscribe and retry...");
          await existing.unsubscribe();

          const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;
          const applicationServerKey = urlBase64ToUint8Array(vapidKey);
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          });
          return subscription;
        }
      } catch (retryErr) {
        console.error("Retry also failed:", retryErr);
      }
    }

    return null;
  }
}

export async function requestNotificationPermissionAndSubscribe(): Promise<PushSubscription | null> {
  if (!("Notification" in globalThis)) {
    console.warn("Notifications API not supported.");
    return null;
  }

  let permission = Notification.permission;
  if (permission !== "granted") {
    try {
      permission = await Notification.requestPermission();
    } catch (err) {
      console.error("Notification permission request failed:", err);
      return null;
    }
  }

  if (permission !== "granted") {
    console.warn("Notification permission not granted.");
    return null;
  }

  return await subscribeUserToPush();
}

export async function persistSubscription(
  userId: string,
  subscription: PushSubscription,
): Promise<boolean> {
  try {
    // Convert PushSubscription to JSON-serializable object
    const subscriptionJson = subscription.toJSON();

    await createPushSubscription({
      userId,
      platform: "web",
      type: "webpush",
      subscriptionObject: subscriptionJson as WebPushConfig,
    });
    return true;
  } catch (err: any) {
    console.error("Failed to persist push subscription:", err);
    if (err?.response?.status === 409) {
      // Already exists
      return true;
    }
    return false;
  }
}
